import {
  createRow,
  memo,
  type RowData,
  type Table,
  type RowModel,
  type Row,
  getMemoOptions,
} from '@tanstack/table-core';

export function getCoreRowModel<TData extends RowData>(): (
  table: Table<TData>,
) => () => RowModel<TData> {
  return (table) =>
    memo(
      // Picked base row model from the tanstack/table-core but added table.options.columns
      // here to make sure the table re-inits when columns are changed.
      () => [table.options.data, table.options.columns],
      (
        data,
      ): {
        rows: Row<TData>[];
        flatRows: Row<TData>[];
        rowsById: Record<string, Row<TData>>;
      } => {
        const rowModel: RowModel<TData> = {
          rows: [],
          flatRows: [],
          rowsById: {},
        };

        const accessRows = (
          originalRows: TData[],
          depth = 0,
          parentRow?: Row<TData>,
        ): Row<TData>[] => {
          const rows = [] as Row<TData>[];

          for (let i = 0; i < originalRows.length; i++) {
            // Make the row
            const row = createRow(
              table,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              table._getRowId(originalRows[i]!, i, parentRow),
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              originalRows[i]!,
              i,
              depth,
              undefined,
              parentRow?.id,
            );

            // Keep track of every row in a flat array
            rowModel.flatRows.push(row);
            // Also keep track of every row by its ID
            rowModel.rowsById[row.id] = row;
            // Push table row into parent
            rows.push(row);

            // Get the original subrows
            if (table.options.getSubRows) {
              row.originalSubRows = table.options.getSubRows(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                originalRows[i]!,
                i,
              );

              // Then recursively access them
              if (row.originalSubRows?.length) {
                row.subRows = accessRows(row.originalSubRows, depth + 1, row);
              }
            }
          }

          return rows;
        };

        rowModel.rows = accessRows(data);

        return rowModel;
      },
      getMemoOptions(table.options, 'debugTable', 'getRowModel', () =>
        table._autoResetPageIndex(),
      ),
    );
}
